import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { calcTotals, money, reveal, NPN, IMAGES } from "@/components/store/data";

export default function Checkout({ qty, setQty, onSubmit, submitting }) {
  const [form, setForm] = useState({ email: "", full_name: "", location: "" });
  const { subtotal, discount, rate, shipping, total } = calcTotals(qty);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <section id="checkout" className="relative py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-5">Checkout</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F4F1EB]">Complete your order</h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <form data-testid="checkout-form" onSubmit={submit} className="lg:col-span-7 space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#B8C5C4] mb-2">Email Address</label>
              <input
                data-testid="input-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@email.com"
                className="w-full bg-[#0C3F3B] border border-white/10 px-4 py-4 text-[#F4F1EB] placeholder:text-[#B8C5C4]/50 rounded-sm"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#B8C5C4] mb-2">Full Name</label>
              <input
                data-testid="input-name"
                type="text"
                required
                value={form.full_name}
                onChange={set("full_name")}
                placeholder="Jane Doe"
                className="w-full bg-[#0C3F3B] border border-white/10 px-4 py-4 text-[#F4F1EB] placeholder:text-[#B8C5C4]/50 rounded-sm"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#B8C5C4] mb-2">Shipping Location</label>
              <textarea
                data-testid="input-location"
                required
                rows={3}
                value={form.location}
                onChange={set("location")}
                placeholder="Street, City, Province, Postal Code, Country"
                className="w-full bg-[#0C3F3B] border border-white/10 px-4 py-4 text-[#F4F1EB] placeholder:text-[#B8C5C4]/50 rounded-sm resize-none"
              />
            </div>
            <p className="text-xs text-[#B8C5C4]/70 leading-relaxed">
              Your IP address and browser (software used) are captured automatically with your order for security and record keeping.
            </p>

            <button
              data-testid="place-order-btn"
              type="submit"
              disabled={submitting || qty < 1}
              className="w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-[#052221] px-7 py-5 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#F0C851] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Processing…" : `Place order & Pay with PayPal · ${money(total)} CAD`}
            </button>
            {qty < 1 && (
              <p data-testid="empty-basket-note" className="text-xs text-[#E06B5E]">Add at least one bottle to your basket to order.</p>
            )}
          </form>

          {/* Summary */}
          <div className="lg:col-span-5">
            <div className="sticky top-24 bg-[#08302D] border border-white/10 rounded-sm p-7">
              <div className="flex items-center gap-4 pb-6 border-b border-white/10">
                <img src={IMAGES.heroBottle} alt="bottle" className="w-16 h-16 object-cover rounded-sm border border-white/10" />
                <div>
                  <p className="font-display text-lg text-[#F4F1EB]">Ubiquinol CoQ10</p>
                  <p className="text-xs text-[#B8C5C4]">NPN {NPN}</p>
                </div>
              </div>

              <div className="flex items-center justify-between py-6 border-b border-white/10">
                <span className="text-sm text-[#B8C5C4] uppercase tracking-[0.15em]">Bottles</span>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    data-testid="summary-decrement"
                    onClick={() => setQty((q) => Math.max(0, q - 1))}
                    className="w-9 h-9 flex items-center justify-center border border-white/20 text-[#F4F1EB] hover:border-[#D4AF37] transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span data-testid="summary-qty" className="font-display text-2xl text-[#F4F1EB] w-8 text-center">{qty}</span>
                  <button
                    type="button"
                    data-testid="summary-increment"
                    onClick={() => setQty((q) => q + 1)}
                    className="w-9 h-9 flex items-center justify-center border border-white/20 text-[#F4F1EB] hover:border-[#D4AF37] transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {discount === 0 && qty > 0 && (
                <p data-testid="discount-hint" className="text-xs text-[#D4AF37] pt-4">Add {3 - qty > 0 ? 3 - qty : 0} more to unlock 10% off · 6+ save 15%</p>
              )}
              <div className="space-y-3 py-6 text-sm">
                <div className="flex justify-between text-[#B8C5C4]"><span>Subtotal</span><span data-testid="summary-subtotal">{money(subtotal)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-[#63A375]" data-testid="summary-discount-row">
                    <span>Volume discount ({Math.round(rate * 100)}%)</span>
                    <span data-testid="summary-discount">-{money(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#B8C5C4]"><span>Shipping</span><span data-testid="summary-shipping">{money(shipping)}</span></div>
              </div>
              <div className="flex justify-between items-baseline pt-5 border-t border-white/10">
                <span className="text-sm uppercase tracking-[0.15em] text-[#F4F1EB]">Total</span>
                <span data-testid="summary-total" className="font-display text-3xl text-[#D4AF37]">{money(total)} <span className="text-sm text-[#B8C5C4]">CAD</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
